


import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { supplierOptions, paymentMethodOptions, meterTypeOptions, normalizeSupplier } from "../../data/formDefaults";

// ── INPUT CLS ──────────────────────────────────────────
function inputCls(hasError = false) {
  return `flex-1 border px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-1
    ${hasError ? "border-red-500 focus:ring-red-400" : "border-gray-400 focus:ring-blue-500"}`;
}

function selectCls(hasError = false) {
  return `flex-1 border px-3 py-1 text-sm text-gray-900 bg-white focus:outline-none focus:ring-1
    ${hasError ? "border-red-500 focus:ring-red-400" : "border-gray-400 focus:ring-blue-500"}`;
}

// ── SECTION CARD ───────────────────────────────────────
function SectionCard({ title, subtitle, children }) {
  return (
    <div className="border border-gray-300 bg-[#dbe9f5] p-4">
      <div className="mb-4 border-b border-cyan-500 pb-2">
        <h3 className="text-sm font-bold uppercase text-gray-700">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// ── ROW FIELD WRAPPER ──────────────────────────────────
function FieldRow({ label, labelWidth = "w-44", required, children }) {
  return (
    <div className="flex items-start gap-3">
      <label className={`${labelWidth} text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0`}>
        {label}{required && " *"}
      </label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

// ── HELPERS ────────────────────────────────────────────
function toDate(val) {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d) ? "" : d.toISOString().slice(0, 10);
}
function toDateTime(val) {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d) ? "" : d.toISOString().slice(0, 16);
}
function getAudioMime(filename) {
  if (!filename) return "audio/mpeg";
  const ext = filename.split(".").pop().toLowerCase();
  const map = { mp3: "audio/mpeg", wav: "audio/wav", mp4: "audio/mp4", ogg: "audio/ogg", webm: "audio/webm", aac: "audio/aac", flac: "audio/flac", opus: "audio/opus", vox: "audio/vox" };
  return map[ext] || "audio/mpeg";
}
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function getFileIcon(fileName = "") {
  const ext = fileName.split(".").pop().toLowerCase();
  if (ext === "png") return "🖼️";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  return "📎";
}

const BILL_RANGE_OPTIONS = [
  "£0 - £500", "£500 - £1,000", "£1,000 - £2,500",
  "£2,500 - £5,000", "£5,000 - £10,000", "£10,000 - £25,000",
  "£25,000 - £50,000", "£50,000+",
];

const DISPOSITION_OPTIONS = [
  "Quality", "Quality Low AQ", "Quality OO",
  "Callback",
];
const DISPOSITION_OPTIONS_ADMIN = [
  ...DISPOSITION_OPTIONS,
  "Validate", "Validate Low AQ", "Validate OO",
  "Hold","Sold", "Reject",
];

// ── MAIN ───────────────────────────────────────────────
export default function EditLead() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const canEditQuality = user?.role === "admin" || user?.role === "quality";

  const [formData, setFormData] = useState({});
  const [newVoxFiles, setNewVoxFiles] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);   // ← NEW
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ── FETCH ──────────────────────────────────────────
  useEffect(() => {
    const fetchLead = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/leads/${id}`);
        const lead = res.data.data;

        const elec = lead.electricity_details || {};
        const gas = lead.gas_details || {};
        const review = lead.quality_review || {};

        setFormData({
          // ── LEAD CORE ────────────────────────────────
          id: lead.id,
          lead_date: toDate(lead.lead_date),
          validation_type: lead.validation_type || "",
          business_name: lead.business_name || "",
          contact_name: lead.contact_name || "",
          billing_name: lead.billing_name || "",
          business_address: lead.business_address || "",
          postal_code: lead.postal_code || "",
          business_phone: lead.business_phone || "",
          created_by: lead.created_by || "",
          created_by_name: lead.created_by_name || "",
          created_at: lead.created_at || "",
          updated_at: lead.updated_at || "",

          // ── ELECTRICITY ───────────────────────────────
          electricity_supplier: normalizeSupplier(elec.current_supplier || ""),
          electricity_customer_account_no: elec.customer_account_no || "",
          electricity_month_year: elec.month_and_year || "",
          electricity_contract_end: toDate(elec.contract_end_date),
          electricity_payment_method: elec.payment_method || "",
          meter_type: elec.meter_type || "",
          electricity_day_rate: elec.day_rate || "",
          electricity_night_rate: elec.night_rate || "",
          electricity_eve_rate: elec.eve_weekend_rate || "",
          electricity_standing_charge: elec.standing_charge || "",
          electricity_eac_rate: elec.eac_rate || "",
          electricity_avg_bill: elec.average_bill_amount || "",
          electricity_bill_type: elec.bill_type || "MONTHLY",
          electricity_bill_range: elec.average_bill_range || "",
          electricity_msn: elec.msn_no || "",
          mpan_core: elec.mpan_core || "",
          mpan_top_line: elec.mpan_top_line || "",

          // ── GAS ───────────────────────────────────────
          gas_supplier: normalizeSupplier(gas.current_supplier || ""),
          gas_customer_account_no: gas.customer_account_no || "",
          gas_month_year: gas.month_and_year || "",
          gas_contract_end: toDate(gas.contract_end_date),
          gas_payment_method: gas.payment_method || "",
          gas_annual_consumption: gas.annual_consumption ?? "",
          gas_day_rate: gas.day_rate || "",
          gas_standing_charge: gas.standing_charge || "",
          mprn_no: gas.mprn_no || "",
          gas_avg_bill: gas.average_bill_amount || "",
          gas_billing_freq: gas.billing_frequency || "MONTHLY",
          gas_bill_range: gas.average_bill_range || "",
          gas_msn: gas.msn_no || "",

          // ── QUALITY REVIEW ────────────────────────────
          email: review.email || "",
          lead_disposition: review.lead_disposition || "",
          agent_feedback: review.agent_feedback || "",
          quality_feedback: review.quality_feedback || "",
          callback_date: toDateTime(review.callback_date),

          // ── VOX FILES ─────────────────────────────────
          existing_vox_files: lead.vox_files || [],

          // ── ATTACHMENTS ───────────────────────────────  ← NEW
          existing_attachments: lead.attachments || [],
        });

      } catch (err) {
        setError(err.response?.data?.message || "Failed to load lead.");
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id]);

  // ── HANDLERS ───────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewVoxFileChange = (e) => {
    setNewVoxFiles((prev) => [...prev, ...Array.from(e.target.files)]);
  };

  const removeNewVoxFile = (index) => {
    setNewVoxFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // const removeExistingVoxFile = (fileId) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     existing_vox_files: prev.existing_vox_files.filter((f) => f.id !== fileId),
  //   }));
  // };

  // ── ATTACHMENT HANDLERS ───────────────────────────  ← NEW
  const handleNewAttachmentChange = (e) => {
    setNewAttachments((prev) => [...prev, ...Array.from(e.target.files)]);

  };

  const removeNewAttachment = (index) => {
    setNewAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // const removeExistingAttachment = (fileId) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     existing_attachments: prev.existing_attachments.filter((a) => a.id !== fileId),
  //   }));
  // };

  // Stable preview URLs for new files
  const previewUrls = useMemo(() => {
    return newVoxFiles.map((f) => URL.createObjectURL(f));
  }, [newVoxFiles]);

  useEffect(() => {
    return () => previewUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  // ── SUBMIT ─────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      const payload = new FormData();

      // ── LEAD CORE ──────────────────────────────────
      payload.append("lead_date", formData.lead_date || "");
      payload.append("validation_type", formData.validation_type || "");
      payload.append("business_name", formData.business_name || "");
      payload.append("contact_name", formData.contact_name || "");
      payload.append("billing_name", formData.billing_name || "");
      payload.append("business_address", formData.business_address || "");
      payload.append("postal_code", formData.postal_code || "");
      payload.append("business_phone", formData.business_phone || "");

      // ── ELECTRICITY ────────────────────────────────
      payload.append("electricity_supplier", formData.electricity_supplier || "");
      payload.append("electricity_customer_account_no", formData.electricity_customer_account_no || "");
      payload.append("electricity_month_year", formData.electricity_month_year || "");
      payload.append("electricity_contract_end", formData.electricity_contract_end || "");
      payload.append("electricity_payment_method", formData.electricity_payment_method || "");
      payload.append("meter_type", formData.meter_type || "");
      payload.append("electricity_day_rate", formData.electricity_day_rate || "");
      payload.append("electricity_night_rate", formData.electricity_night_rate || "");
      payload.append("electricity_eve_rate", formData.electricity_eve_rate || "");
      payload.append("electricity_standing_charge", formData.electricity_standing_charge || "");
      payload.append("electricity_eac_rate", formData.electricity_eac_rate || "");
      payload.append("electricity_avg_bill", formData.electricity_avg_bill || "");
      payload.append("electricity_bill_type", formData.electricity_bill_type || "MONTHLY");
      payload.append("electricity_bill_range", formData.electricity_bill_range || "");
      payload.append("electricity_msn", formData.electricity_msn || "");

      payload.append("mpan_top_line", formData.mpan_top_line || "");
      payload.append("mpan_core", formData.mpan_core || "");

      // ── GAS ────────────────────────────────────────
      payload.append("gas_supplier", formData.gas_supplier || "");
      payload.append("gas_customer_account_no", formData.gas_customer_account_no || "");
      payload.append("gas_month_year", formData.gas_month_year || "");
      payload.append("gas_contract_end", formData.gas_contract_end || "");
      payload.append("gas_payment_method", formData.gas_payment_method || "");
      payload.append("gas_annual_consumption", formData.gas_annual_consumption ?? "");
      payload.append("gas_day_rate", formData.gas_day_rate || "");
      payload.append("gas_standing_charge", formData.gas_standing_charge || "");
      payload.append("mprn_no", formData.mprn_no || "");
      payload.append("gas_avg_bill", formData.gas_avg_bill || "");
      payload.append("gas_billing_freq", formData.gas_billing_freq || "MONTHLY");
      payload.append("gas_bill_range", formData.gas_bill_range || "");
      payload.append("gas_msn", formData.gas_msn || "");

      // ── QUALITY REVIEW ─────────────────────────────
      payload.append("email", formData.email || "");
      payload.append("lead_disposition", formData.lead_disposition || "");
      payload.append("agent_feedback", formData.agent_feedback || "");
      payload.append("quality_feedback", formData.quality_feedback || "");
      if (formData.callback_date) {
        payload.append("callback_date", formData.callback_date);
      }

      // ── NEW VOX FILES ──────────────────────────────
      newVoxFiles.forEach((file) => payload.append("vox_files", file));

      // ── NEW ATTACHMENTS ────────────────────────────  ← NEW
      newAttachments.forEach((file) => payload.append("attachments", file));

      await axiosInstance.put(`/leads/${id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ── SUCCESS ─────────────────────────────────  ← CHANGED
      await Swal.fire({
        icon: "success",
        title: "Lead Updated!",
        text: "The lead has been updated successfully.",
        confirmButtonColor: "#2563eb",
        timer: 1500,
        showConfirmButton: false,
      });

      const d = formData.lead_disposition?.trim();
      if (d === "Validate") navigate("/validate");
      else if (d === "Validate OO") navigate("/validate-00");
      else if (d === "Validate Low AQ") navigate("/validate-low-aq");
      else if (d === "Quality") navigate("/quality-leads");
      else if (d === "Quality OO") navigate("/quality00-leads");
      else if (d === "Quality Low AQ") navigate("/qualitylowaq-leads");
      else if (d === "Reject") navigate("/rejects");
      else if (d === "Hold") navigate("/hold");
      else if (d === "Sold") navigate("/sold");
      else if (d === "Callback") navigate("/callback-leads");
      else navigate("/leadslist");

    } catch (err) {
      console.error(err);

      await Swal.fire({
        icon: "error",
        title: "Update Failed!",
        text: "Failed to update the lead.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── STATES ─────────────────────────────────────────
  if (loading) return (
    <div className="p-6">
      <div className="bg-white border p-10 text-center text-slate-500">Loading...</div>
    </div>
  );
  if (error) return (
    <div className="p-6">
      <div className="bg-white border p-10 text-center text-red-500">{error}</div>
    </div>
  );

  const showElec = formData.validation_type === "elec" || formData.validation_type === "both";
  const showGas = formData.validation_type === "gas" || formData.validation_type === "both";
  const dispositionOptions = canEditQuality ? DISPOSITION_OPTIONS_ADMIN : DISPOSITION_OPTIONS;

  return (
    <div className="text-slate-800">

      {/* ── HEADER ──────────────────────────────────── */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
            Edit Lead
          </h1>

          <nav className="flex items-center text-xs text-slate-400 mt-1">
            <span>Leads</span>
            <span className="mx-2 text-slate-300">›</span>
            <span className="text-slate-600 font-medium">
              Edit #{formData.id}
            </span>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold px-3.5 py-2 rounded-lg whitespace-nowrap">
            {formData.validation_type === "elec"
              ? "Electricity Only"
              : formData.validation_type === "gas"
                ? "Gas Only"
                : formData.validation_type === "both"
                  ? "Electricity + Gas"
                  : "—"}
          </span>

          <button
            type="button"
            onClick={() => navigate("/leadslist")}
            className="
        inline-flex items-center justify-center
        px-4 py-2
        border border-slate-300
        rounded-lg
        bg-white
        text-slate-700
        text-sm font-semibold
        hover:bg-slate-50
        hover:border-slate-400
        transition-colors
        shadow-sm
      "
          >
            ← Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── GENERAL INFORMATION ─────────────────────── */}
        <SectionCard title="General Information">
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">

            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-500 uppercase shrink-0">Lead ID</label>
              <input disabled value={`#${formData.id}`}
                className="flex-1 border border-gray-400 px-5 py-1 text-sm bg-gray-100 text-gray-600" />
            </div>
            <div />

            <FieldRow label="Validate Type" required>
              <select name="validation_type" value={formData.validation_type || ""} onChange={handleChange}
                className={selectCls()}>
                <option value="">Select Type</option>
                <option value="elec">Electricity Only</option>
                <option value="gas">Gas Only</option>
                <option value="both">Electricity + Gas</option>
              </select>
            </FieldRow>
            <div className="flex items-center gap-3">
              <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Agent ID</label>
              <input disabled value={formData.created_by_name || ""}
                className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-gray-100 text-gray-600" />
            </div>

            <FieldRow label="Lead Date">
              <input type="date" name="lead_date" value={formData.lead_date || ""} onChange={handleChange}
                className={inputCls()} />
            </FieldRow>
            <div className="flex items-center gap-3">
              <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Customer Name *</label>
              <input type="text" name="contact_name" value={formData.contact_name || ""} onChange={handleChange}
                placeholder="Enter customer name" className={inputCls()} />
            </div>

            <FieldRow label="Business Name" required>
              <input type="text" name="business_name" value={formData.business_name || ""} onChange={handleChange}
                placeholder="Enter business name" className={inputCls()} />
            </FieldRow>
            <div className="flex items-center gap-3">
              <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Billing Name *</label>
              <input type="text" name="billing_name" value={formData.billing_name || ""} onChange={handleChange}
                placeholder="Enter billing name" className={inputCls()} />
            </div>

            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                Business Address *
              </label>
              <textarea name="business_address" rows={6} value={formData.business_address || ""} onChange={handleChange}
                placeholder="Enter business address"
                className="w-full border border-gray-400 px-4 py-2 text-sm text-gray-900 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Phone No *</label>
                <input type="tel" name="business_phone" value={formData.business_phone || ""} onChange={handleChange}
                  placeholder="Enter phone number" className={inputCls()} />
              </div>

              {showElec && (
                <div className="flex items-center gap-3">
                  <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">MPAN</label>
                  <input type="text" name="mpan_core" value={formData.mpan_core || ""} onChange={handleChange}
                    placeholder="Enter 13 digit MPAN" maxLength={13} className={`${inputCls()} font-mono`} />
                </div>
              )}
            </div>

            <FieldRow label="Business Postal Code" required>
              <input type="text" name="postal_code" value={formData.postal_code || ""} onChange={handleChange}
                placeholder="Enter postal code" className={inputCls()} />
            </FieldRow>
            <div />

          </div>
        </SectionCard>

        {/* ── ELECTRICITY ─────────────────────────────── */}
        {showElec && (
          <SectionCard title="Electricity">
            <div className="space-y-3">

              <FieldRow label="Current Supplier" required>
                <select name="electricity_supplier" value={formData.electricity_supplier || ""} onChange={handleChange}
                  className={selectCls()}>
                  <option value="">Select Supplier</option>
                  {supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Customer Account No">
                <input type="text" name="electricity_customer_account_no"
                  value={formData.electricity_customer_account_no || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <FieldRow label="Month And Year">
                <input type="month" name="electricity_month_year"
                  value={formData.electricity_month_year || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <FieldRow label="Contract End Date" required>
                <input type="date" name="electricity_contract_end"
                  value={formData.electricity_contract_end || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <FieldRow label="Payment Method">
                <select name="electricity_payment_method"
                  value={formData.electricity_payment_method || ""} onChange={handleChange}
                  className={selectCls()}>
                  <option value="">Select Payment Method</option>
                  {paymentMethodOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Meter Type">
                <select name="meter_type" value={formData.meter_type || ""} onChange={handleChange}
                  className={selectCls()}>
                  <option value="">Select Meter</option>
                  {meterTypeOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </FieldRow>

              <div className="border-t border-cyan-500 pt-3 mt-2">
                <h3 className="text-[13px] font-bold uppercase text-gray-700 mb-3">Current Rate</h3>
                <div className="space-y-3">
                  {[
                    { label: "Day", name: "electricity_day_rate" },
                    { label: "Night", name: "electricity_night_rate" },
                    { label: "Eve/Weekends", name: "electricity_eve_rate" },
                    { label: "Standing Charge", name: "electricity_standing_charge" },
                    { label: "EAC *", name: "electricity_eac_rate" },
                  ].map(({ label, name }) => (
                    <FieldRow key={name} label={label}>
                      <input type="text" name={name} value={formData[name] || ""} onChange={handleChange}
                        className={inputCls()} />
                    </FieldRow>
                  ))}
                </div>
              </div>

              <FieldRow label="Supply No *">
                <SupplyNoDisplay
                  topLine={formData.mpan_top_line || ""}
                  core={formData.mpan_core || ""}
                  onChange={(name, value) => {
                    setFormData((prev) => ({
                      ...prev,
                      [name]: value,
                    }));
                  }}
                />
              </FieldRow>

              <div className="flex items-center gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">
                  Average Bill Amount
                </label>
                <div className="flex flex-1 gap-2">
                  <input type="text" name="electricity_avg_bill" value={formData.electricity_avg_bill || ""}
                    onChange={handleChange} className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <select name="electricity_bill_type" value={formData.electricity_bill_type || "MONTHLY"}
                    onChange={handleChange}
                    className="w-32 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                    <option value="MONTHLY">MONTHLY</option>
                    <option value="QUARTERLY">QUARTERLY</option>
                  </select>
                </div>
              </div>

              <FieldRow label="Average Bill Range">
                <select name="electricity_bill_range" value={formData.electricity_bill_range || ""}
                  onChange={handleChange} className={selectCls()}>
                  <option value="">Select</option>
                  {BILL_RANGE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="MSN No">
                <input type="text" name="electricity_msn" value={formData.electricity_msn || ""}
                  onChange={handleChange} className={`${inputCls()} font-mono`} />
              </FieldRow>

            </div>
          </SectionCard>
        )}

        {/* ── GAS ─────────────────────────────────────── */}
        {showGas && (
          <SectionCard title="Gas">
            <div className="space-y-3">

              <FieldRow label="Current Supplier" required>
                <select name="gas_supplier" value={formData.gas_supplier || ""} onChange={handleChange}
                  className={selectCls()}>
                  <option value="">Select Supplier</option>
                  {supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Customer Account No">
                <input type="text" name="gas_customer_account_no"
                  value={formData.gas_customer_account_no || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <FieldRow label="Month And Year">
                <input type="month" name="gas_month_year"
                  value={formData.gas_month_year || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <FieldRow label="Contract End Date" required>
                <input type="date" name="gas_contract_end"
                  value={formData.gas_contract_end || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <FieldRow label="Payment Method">
                <select name="gas_payment_method" value={formData.gas_payment_method || ""} onChange={handleChange}
                  className={selectCls()}>
                  <option value="">Select Payment Method</option>
                  {paymentMethodOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="Annual Consumption" required>
                <input type="text" name="gas_annual_consumption"
                  value={formData.gas_annual_consumption ?? ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>

              <div className="border-t border-cyan-500 pt-3 mt-2">
                <h3 className="text-[13px] font-bold uppercase text-gray-700 mb-3">Current Rate</h3>
                <div className="space-y-3">
                  {[
                    { label: "Day", name: "gas_day_rate" },
                    { label: "Standing Charge", name: "gas_standing_charge" },
                  ].map(({ label, name }) => (
                    <FieldRow key={name} label={label}>
                      <input type="text" name={name} value={formData[name] || ""} onChange={handleChange}
                        className={inputCls()} />
                    </FieldRow>
                  ))}

                  <FieldRow label="MPR No" required>
                    <input type="text" name="mprn_no" value={formData.mprn_no || ""} onChange={handleChange}
                      className={`${inputCls()} font-mono`} />
                  </FieldRow>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">
                  Average Bill Amount
                </label>
                <div className="flex flex-1 gap-2">
                  <input type="text" name="gas_avg_bill" value={formData.gas_avg_bill || ""}
                    onChange={handleChange}
                    className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <select name="gas_billing_freq" value={formData.gas_billing_freq || "MONTHLY"}
                    onChange={handleChange}
                    className="w-32 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                    <option value="MONTHLY">MONTHLY</option>
                    <option value="QUARTERLY">QUARTERLY</option>
                  </select>
                </div>
              </div>

              <FieldRow label="Average Bill Range">
                <select name="gas_bill_range" value={formData.gas_bill_range || ""} onChange={handleChange}
                  className={selectCls()}>
                  <option value="">Select</option>
                  {BILL_RANGE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </FieldRow>

              <FieldRow label="MSN No">
                <input type="text" name="gas_msn" value={formData.gas_msn || ""}
                  onChange={handleChange} className={`${inputCls()} font-mono`} />
              </FieldRow>

            </div>
          </SectionCard>
        )}

        {/* ── QUALITY REVIEW ───────────────────────────── */}
        <div className="border border-gray-300 bg-[#dbe9f5] p-4">
          <div className="mb-4 border-b border-cyan-500 pb-2">
            <h3 className="text-sm font-bold uppercase text-gray-700">Quality Review</h3>
            <p className="text-xs text-gray-500 mt-1">Only the quality team can edit this section</p>
          </div>

          <div className="space-y-3">

            <FieldRow label="Email">
              <input type="email" name="email" value={formData.email || ""} onChange={handleChange}
                placeholder="Enter email" className={inputCls()} />
            </FieldRow>

            {formData.lead_disposition === "Callback" && (
              <FieldRow label="Callback Date">
                <input type="datetime-local" name="callback_date"
                  value={formData.callback_date || ""} onChange={handleChange}
                  className={inputCls()} />
              </FieldRow>
            )}

            <FieldRow label="Lead Disposition" required>
              <select name="lead_disposition" value={formData.lead_disposition || ""} onChange={handleChange}
                className={`w-full border border-gray-400 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500`}>
                <option value="">Select Disposition</option>
                {dispositionOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </FieldRow>

            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                Agent Feedback
              </label>
              <textarea name="agent_feedback" rows={5} value={formData.agent_feedback || ""}
                onChange={handleChange} placeholder="Enter agent feedback..."
                className="flex-1 border border-gray-400 px-3 py-2 text-sm text-gray-900 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>

            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                Quality Feedback
              </label>
              <textarea name="quality_feedback" rows={5} value={formData.quality_feedback || ""}
                onChange={handleChange} disabled={!canEditQuality}
                placeholder="Quality team feedback..."
                className={`flex-1 border px-3 py-2 text-sm rounded-md resize-none focus:outline-none focus:ring-1
                  ${canEditQuality
                    ? "border-gray-400 bg-white text-gray-900 focus:ring-blue-500"
                    : "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"}`} />
            </div>

            {/* EXISTING VOX FILES */}
            {formData.existing_vox_files?.length > 0 && (
              <div className="flex items-start gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                  Existing Recordings
                </label>
                <div className="flex-1 space-y-3">
                  {formData.existing_vox_files.map((f) => (
                    <div key={f.id} className="bg-white border border-gray-300 rounded px-3 py-2 text-xs">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">
                          🎵 {f.vox_file}
                          {f.created_at && (
                            <span className="text-gray-400 font-normal ml-2">
                              ({new Date(f.created_at).toLocaleString("en-GB")})
                            </span>
                          )}
                        </span>
                        {/* <button type="button" onClick={() => removeExistingVoxFile(f.id)}
                          className="text-red-500 hover:text-red-700 font-semibold shrink-0 ml-2">
                          Remove
                        </button> */}
                      </div>
                      <audio controls preload="auto" className="w-full">
                        <source src={`https://ratcrm.zentelex.com/uploads/${f.vox_file}`} type={getAudioMime(f.vox_file)} />
                      </audio>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ADD NEW VOX FILES */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                Upload VOX Files
              </label>
              <div className="flex-1">
                <input type="file"
                  accept=".vox,.mp3,.wav,.mp4,.ogg,.webm,.aac,.flac,.opus"
                  multiple onChange={handleNewVoxFileChange}
                  className="w-full border border-gray-400 px-3 py-2 text-sm bg-white file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-blue-600 file:text-white file:text-xs file:font-semibold" />
                <p className="text-[11px] text-gray-500 mt-1">
                  Hold Ctrl (Windows) or Cmd (Mac) to select multiple files.
                </p>

                {newVoxFiles.length > 0 && (
                  <ul className="mt-2 space-y-3">
                    {newVoxFiles.map((file, index) => (
                      <li key={`${file.name}-${index}`}
                        className="bg-white border border-gray-300 rounded px-3 py-2 text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-700 font-medium">
                            🎵 {file.name}{" "}
                            <span className="text-gray-400 font-normal">
                              ({formatFileSize(file.size)})
                            </span>
                          </span>
                          <button type="button" onClick={() => removeNewVoxFile(index)}
                            className="text-red-500 hover:text-red-700 font-semibold shrink-0 ml-2">
                            Remove
                          </button>
                        </div>
                        {previewUrls[index] && (
                          <audio key={previewUrls[index]} controls preload="auto" className="w-full"
                            src={previewUrls[index]} />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* ── EXISTING ATTACHMENTS ─────────────────  ← NEW */}
            {formData.existing_attachments?.length > 0 && (
              <div className="flex items-start gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                  Existing Attachments
                </label>
                <div className="flex-1 space-y-2">
                  {formData.existing_attachments.map((a) => (
                    <div key={a.id}
                      className="flex items-center justify-between bg-white border border-gray-300 rounded px-3 py-2 text-xs">
                      <a
                        href={`https://ratcrm.zentelex.com/uploads/${a.file_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:underline font-medium truncate pr-2"
                      >
                        {getFileIcon(a.original_name)} {a.original_name}
                        {a.created_at && (
                          <span className="text-gray-400 font-normal">
                            ({new Date(a.created_at).toLocaleString("en-GB")})
                          </span>
                        )}
                      </a>
                      {/* <button type="button" onClick={() => removeExistingAttachment(a.id)}
                        className="text-red-500 hover:text-red-700 font-semibold shrink-0 ml-2">
                        Remove
                      </button> */}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── ADD NEW ATTACHMENTS ──────────────────  ← NEW */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                Upload Attachments
              </label>
              <div className="flex-1">
                <input
                  type="file"
                  accept=".png,image/png,.pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  multiple onChange={handleNewAttachmentChange}
                  className="w-full border border-gray-400 px-3 py-2 text-sm bg-white file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-blue-600 file:text-white file:text-xs file:font-semibold" />
                <p className="text-[11px] text-gray-500 mt-1">
                  Accepted formats: PNG, PDF, Word (.doc/.docx). Hold Ctrl/Cmd to select multiple.
                </p>

                {newAttachments.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {newAttachments.map((file, index) => (
                      <li key={`${file.name}-${index}`}
                        className="flex items-center justify-between bg-white border border-gray-300 rounded px-3 py-2 text-xs">
                        <span className="text-gray-700 truncate pr-2 font-medium">
                          {getFileIcon(file.name)} {file.name}{" "}
                          <span className="text-gray-400 font-normal">
                            ({formatFileSize(file.size)})
                          </span>
                        </span>
                        <button type="button" onClick={() => removeNewAttachment(index)}
                          className="text-red-500 hover:text-red-700 font-semibold shrink-0 ml-2">
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* ── BUTTONS ──────────────────────────────────── */}
        {canEditQuality &&
          <div className="flex justify-end gap-3 pb-6">
            <button type="button" onClick={() => navigate(-1)}
              className="px-8 py-3 bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold rounded-xl text-sm">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        }
      </form>
    </div>
  );
}


function SupplyNoDisplay({ topLine = "", core = "", onChange }) {
  const topLengths = [2, 4, 3];
  const coreLengths = [2, 4, 4, 3];

  const [topSegs, setTopSegs] = useState(() => splitFixed(topLine, topLengths));
  const [coreSegs, setCoreSegs] = useState(() => splitFixed(core, coreLengths));

  useEffect(() => {
    const incomingJoined = (topLine || "").replace(/\s/g, "");
    const currentJoined = topSegs.join("");
    if (incomingJoined !== currentJoined) {
      setTopSegs(splitFixed(topLine, topLengths));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLine]);

  useEffect(() => {
    const incomingJoined = (core || "").replace(/\s/g, "");
    const currentJoined = coreSegs.join("");
    if (incomingJoined !== currentJoined) {
      setCoreSegs(splitFixed(core, coreLengths));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core]);

  const updateTop = (index, value) => {
    const maxLength = topLengths[index];
    const clean = value.replace(/\D/g, "").slice(0, maxLength);

    const updated = [...topSegs];
    updated[index] = clean;
    setTopSegs(updated);

    onChange("mpan_top_line", joinFixed(updated, topLengths));
  };

  const updateBottom = (index, value) => {
    const maxLength = coreLengths[index];
    const clean = value.replace(/\D/g, "").slice(0, maxLength);

    const updated = [...coreSegs];
    updated[index] = clean;
    setCoreSegs(updated);

    onChange("mpan_core", joinFixed(updated, coreLengths));
  };

  return (
    <div className="inline-block border border-gray-400 bg-white">
      <table className="border-collapse table-fixed">
        <tbody>

          <tr>
            <td
              rowSpan={2}
              className="w-[48px] h-[42px] border-r border-gray-400 text-center align-middle bg-white"
            >
              <span className="text-blue-600 text-xl font-bold">S</span>
            </td>

            {topSegs.map((value, index) => (
              <td key={`top-${index}`} className="h-[42px] border-r border-gray-300 last:border-r-0 p-0">
                <input
                  type="text"
                  value={value}
                  maxLength={topLengths[index]}
                  onChange={(e) => updateTop(index, e.target.value)}
                  className="w-full h-[42px] px-3 text-center text-sm text-gray-800 bg-white border-0 outline-none focus:bg-blue-50 focus:ring-1 focus:ring-blue-500"
                />
              </td>
            ))}
          </tr>

          <tr>
            {coreSegs.map((value, index) => (
              <td key={`bottom-${index}`} className="h-[42px] border-t border-r border-gray-300 last:border-r-0 p-0">
                <input
                  type="text"
                  value={value}
                  maxLength={coreLengths[index]}
                  onChange={(e) => updateBottom(index, e.target.value)}
                  className="w-full h-[42px] px-3 text-center text-sm text-gray-400 bg-white border-0 outline-none focus:bg-blue-50 focus:text-gray-800 focus:ring-1 focus:ring-blue-500"
                />
              </td>
            ))}
          </tr>

        </tbody>
      </table>
    </div>
  );
}

function splitFixed(value, lengths) {
  const digits = (value || "").replace(/\D/g, "");
  const segs = [];
  let pos = 0;
  for (const len of lengths) {
    segs.push(digits.slice(pos, pos + len));
    pos += len;
  }
  return segs;
}

function joinFixed(segs, lengths) {
  return segs
    .map((seg, i) => seg.padEnd(lengths[i], ""))
    .join("")
    .replace(/\s+$/, "");
}


